"use client";
import {
  AbsoluteFill,
  Sequence,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// ════════════════════════════════════════════════════════════════════
// CHOCOLATE OVERLAYS — transparent, word-stamp synced.
// 16fps · 1280x704 · base video 715.88s.
// Every element appears at the exact word_timestamp it refers to.
// NO background — composited onto real footage. NO full-canvas panels.
// ════════════════════════════════════════════════════════════════════

const FPS = 16;
const sToF = (s: number) => Math.round(s * FPS);
const FONT = "Inter, system-ui, sans-serif";
// Strong layered shadow so cream/white text reads on bright footage
const TS = "0 3px 14px rgba(0,0,0,0.92), 0 1px 5px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.9)";
const CREAM = "#f5e8d3";
const GOLD = "#d4a574";
const DIM = "#a0825d";

// fade-in at word time, over `dur` seconds — nothing visible before the word
const appear = (t: number, atS: number, dur = 0.18) =>
  interpolate(t, [atS, atS + dur], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

// full on-screen window: fade in at inS, hold, fade out by outS
const win = (t: number, inS: number, outS: number) =>
  interpolate(t, [inS, inS + 0.2, outS - 0.4, outS], [0, 1, 1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

// ─── 1 · BRAND CHIPS — opening hook, vertical stack, each on its word ──
const BrandChips: React.FC<{ s: number }> = ({ s }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, 2.528, 7.364);
  const brands = [
    { name: "CADBURY", atS: 2.633 },
    { name: "HERSHEY'S", atS: 3.277 },
    { name: "LINDT", atS: 4.25 },
  ];
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {brands.map((b) => {
          const o = appear(t, b.atS, 0.16);
          const x = interpolate(t, [b.atS, b.atS + 0.22], [-30, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <div key={b.name} style={{ opacity: o, transform: `translateX(${x}px)`, color: CREAM, fontSize: 40, fontWeight: 800, letterSpacing: "0.12em", textShadow: TS, borderLeft: `4px solid ${GOLD}`, paddingLeft: 18 }}>
              {b.name}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ─── 2 · HOOK TEXT — single punchy line ───────────────────────────────
const HookText: React.FC<{ s: number; text: string; atS: number; outS: number }> = ({ s, text, atS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const sc = interpolate(t, [atS, atS + 0.3], [0.9, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: "#fff", fontSize: 64, fontWeight: 900, letterSpacing: "0.04em", textShadow: TS, transform: `scale(${sc})`, textAlign: "center" }}>
        {text}
      </div>
    </AbsoluteFill>
  );
};

// ─── 3 · STAMP — rotated framed callout ───────────────────────────────
const Stamp: React.FC<{ s: number; title: string; sub?: string; atS: number; outS: number; color?: string }> = ({ s, title, sub, atS, outS, color = GOLD }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const sc = spring({ frame: Math.max(0, frame - sToF(atS - s)), fps, config: { damping: 9, stiffness: 130 } });
  const rot = interpolate(t, [atS, atS + 0.35], [-13, -5], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ transform: `rotate(${rot}deg) scale(${sc})`, border: `4px solid ${color}`, padding: "22px 48px", color, boxShadow: `0 0 28px ${color}44, 0 8px 26px rgba(0,0,0,0.7)` }}>
        <div style={{ fontSize: 52, fontWeight: 900, letterSpacing: "0.07em", textShadow: TS }}>{title}</div>
        {sub && <div style={{ fontSize: 19, marginTop: 7, letterSpacing: "0.2em", textAlign: "center", textShadow: TS }}>{sub}</div>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 4 · BIG STAT — number counts up, locks exactly on the word ───────
const BigStat: React.FC<{
  s: number; label: string; value: number; valueS: number; outS: number;
  decimals?: number; prefix?: string; suffix?: string; sub?: string;
}> = ({ s, label, value, valueS, outS, decimals = 0, prefix, suffix, sub }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const labelS = valueS - 0.25;
  const op = win(t, labelS, outS);
  const cur = interpolate(t, [valueS, valueS + 0.42], [0, value], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const numOp = appear(t, valueS, 0.12);
  const sc = spring({ frame: Math.max(0, frame - sToF(valueS - s)), fps, config: { damping: 13, stiffness: 95 } });
  const subOp = appear(t, valueS + 0.5, 0.3);
  const shown = value >= 100 ? Math.round(cur).toLocaleString() : cur.toFixed(decimals);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: GOLD, fontSize: 25, letterSpacing: "0.24em", marginBottom: 14, textShadow: TS, opacity: appear(t, labelS, 0.2) }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, opacity: numOp, transform: `scale(${sc})` }}>
        {prefix && <span style={{ color: "#fff", fontSize: 52, fontWeight: 800, textShadow: TS }}>{prefix}</span>}
        <span style={{ color: "#fff", fontSize: 168, fontWeight: 800, letterSpacing: "-4px", textShadow: TS, lineHeight: 1 }}>{shown}</span>
        {suffix && <span style={{ color: GOLD, fontSize: 58, fontWeight: 700, textShadow: TS }}>{suffix}</span>}
      </div>
      {sub && <div style={{ color: CREAM, fontSize: 21, letterSpacing: "0.16em", marginTop: 20, textShadow: TS, opacity: subOp }}>{sub}</div>}
    </AbsoluteFill>
  );
};

// ─── 5 · LOWER THIRD — scaled to 0.6 (real numbers, not transform) ────
const LowerThird: React.FC<{
  s: number; name: string; sub: string; italic?: string; nameS: number; subS: number; outS: number;
}> = ({ s, name, sub, italic, nameS, subS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, nameS - 0.2, outS);
  const slideX = interpolate(t, [nameS - 0.25, nameS + 0.15], [-300, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: (v) => 1 - Math.pow(1 - v, 3),
  });
  const nameOp = appear(t, nameS, 0.16);
  const subOp = appear(t, subS, 0.2);
  const accentW = interpolate(t, [nameS - 0.2, nameS + 0.35], [0, 168], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ opacity: op }}>
      <div style={{ position: "absolute", bottom: 86, left: 64, transform: `translateX(${slideX}px)`, fontFamily: FONT }}>
        <div style={{ height: 2, width: accentW, background: `linear-gradient(90deg,${GOLD},#b88a4f)`, marginBottom: 9, boxShadow: `0 0 8px ${GOLD}88` }} />
        <div style={{ color: "#fff", fontSize: 34, fontWeight: 800, letterSpacing: "-0.5px", textShadow: TS, lineHeight: 1.05, opacity: nameOp }}>{name}</div>
        <div style={{ color: GOLD, fontSize: 16, fontWeight: 600, letterSpacing: "0.18em", marginTop: 6, textShadow: TS, opacity: subOp }}>{sub}</div>
        {italic && <div style={{ color: DIM, fontSize: 13, fontStyle: "italic", marginTop: 4, textShadow: TS, opacity: subOp }}>{italic}</div>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 6 · CHAPTER — number on number-word, title on title-word ─────────
const Chapter: React.FC<{ s: number; num: string; title: string; sub?: string; numS: number; titleS: number; outS: number }> = ({ s, num, title, sub, numS, titleS, outS }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const labelS = numS - 0.3;
  const op = win(t, labelS, outS);
  const numScale = spring({ frame: Math.max(0, frame - sToF(numS - s)), fps, config: { damping: 12, stiffness: 85 } });
  const numOp = appear(t, numS, 0.12);
  const lineW = interpolate(t, [titleS - 0.15, titleS + 0.5], [0, 150], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleOp = appear(t, titleS, 0.16);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", flexDirection: "column", opacity: op }}>
      <p style={{ color: DIM, fontSize: 21, fontWeight: 500, letterSpacing: "0.32em", textTransform: "uppercase", margin: 0, marginBottom: 12, textShadow: TS, opacity: appear(t, labelS, 0.2) }}>Step</p>
      <h1 style={{ color: "#fff", fontSize: "9rem", fontWeight: 800, margin: 0, lineHeight: 1, transform: `scale(${numScale})`, textShadow: TS, opacity: numOp }}>{num}</h1>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 20 }}>
        <div style={{ height: 1, width: lineW, backgroundColor: GOLD }} />
        <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: GOLD, opacity: titleOp, boxShadow: `0 0 8px ${GOLD}` }} />
        <div style={{ height: 1, width: lineW, backgroundColor: GOLD }} />
      </div>
      <h2 style={{ color: CREAM, fontSize: "2.5rem", fontWeight: 800, letterSpacing: "0.05em", margin: 0, marginTop: 12, textShadow: TS, opacity: titleOp }}>{title}</h2>
      {sub && <p style={{ color: GOLD, fontSize: "1.15rem", marginTop: 9, letterSpacing: "0.14em", textShadow: TS, opacity: appear(t, titleS + 0.3, 0.25) }}>{sub}</p>}
    </AbsoluteFill>
  );
};

// ─── 7 · TEXT HIGHLIGHT — keyword punches in on its word ──────────────
const TextHighlight: React.FC<{ s: number; pre: string; hi: string; post: string; hiS: number; outS: number; hiColor?: string }> = ({ s, pre, hi, post, hiS, outS, hiColor = CREAM }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const preS = hiS - 0.3;
  const op = win(t, preS, outS);
  const hiOp = appear(t, hiS, 0.14);
  const hiSc = interpolate(t, [hiS, hiS + 0.25], [0.8, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ textAlign: "center", maxWidth: 1080 }}>
        {pre && <span style={{ color: DIM, fontSize: 38, fontWeight: 500, textShadow: TS, opacity: appear(t, preS, 0.2) }}>{pre} </span>}
        <span style={{ color: hiColor, fontSize: 58, fontWeight: 900, letterSpacing: "0.04em", textShadow: `0 0 22px ${hiColor}55, ${TS}`, opacity: hiOp, display: "inline-block", transform: `scale(${hiSc})` }}>{hi}</span>
        {post && <span style={{ color: DIM, fontSize: 38, fontWeight: 500, textShadow: TS, opacity: appear(t, hiS + 0.25, 0.2) }}> {post}</span>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 8 · EQUATION STACK — 3 vertical lines (line1 / op / line2) ───────
const EquationStack: React.FC<{
  s: number; l1: string; op: string; l2: string; l1S: number; opS: number; l2S: number; outS: number; opColor?: string;
}> = ({ s, l1, op, l2, l1S, opS, l2S, outS, opColor = GOLD }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const wOp = win(t, l1S, outS);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: wOp }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <div style={{ color: "#fff", fontSize: 56, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS, opacity: appear(t, l1S, 0.16), transform: `scale(${interpolate(t, [l1S, l1S + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})` }}>{l1}</div>
        <div style={{ color: opColor, fontSize: 44, fontWeight: 800, textShadow: TS, opacity: appear(t, opS, 0.14) }}>{op}</div>
        <div style={{ color: "#fff", fontSize: 56, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS, opacity: appear(t, l2S, 0.16), transform: `scale(${interpolate(t, [l2S, l2S + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})` }}>{l2}</div>
      </div>
    </AbsoluteFill>
  );
};

// ─── 9 · MOISTURE — 60% on word "60", 7% on word "7" ──────────────────
// moisture 231.64 · 60 → 232.71 · down → 233.68 · 7 → 234.20
const MoistureCompare: React.FC<{ s: number }> = ({ s }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, 232.968, 237.66);
  const titleOp = appear(t, 232.968, 0.2);
  const before = Math.round(interpolate(t, [233.933, 234.733], [0, 60], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }));
  const beforeH = interpolate(t, [233.933, 234.733], [0, (60 / 100) * 260], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const leftOp = appear(t, 233.933, 0.14);
  const arrowOp = appear(t, 235.039, 0.16);
  const after = Math.round(interpolate(t, [235.44, 235.978], [0, 7], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }));
  const afterH = interpolate(t, [235.44, 235.978], [0, (7 / 100) * 260], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const rightOp = appear(t, 235.44, 0.14);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ position: "absolute", top: 110, color: GOLD, fontSize: 30, fontWeight: 800, letterSpacing: "0.15em", textShadow: TS, opacity: titleOp }}>MOISTURE LOSS</div>
      <div style={{ display: "flex", gap: 170, alignItems: "flex-end", marginTop: 70 }}>
        <div style={{ textAlign: "center", opacity: leftOp }}>
          <div style={{ color: "#fff", fontSize: 86, fontWeight: 800, textShadow: TS, lineHeight: 1, marginBottom: 10 }}>{before}<span style={{ fontSize: 46 }}>%</span></div>
          <div style={{ width: 120, height: beforeH, background: "linear-gradient(180deg,#c69a73,#8b6647)", margin: "0 auto", borderRadius: 4, boxShadow: "0 6px 24px rgba(0,0,0,0.7)" }} />
          <div style={{ color: CREAM, fontSize: 20, marginTop: 14, letterSpacing: "0.1em", textShadow: TS }}>RAW BEAN</div>
        </div>
        <div style={{ alignSelf: "center", color: GOLD, fontSize: 54, textShadow: TS, opacity: arrowOp }}>→</div>
        <div style={{ textAlign: "center", opacity: rightOp }}>
          <div style={{ color: "#fff", fontSize: 86, fontWeight: 800, textShadow: TS, lineHeight: 1, marginBottom: 10 }}>{after}<span style={{ fontSize: 46 }}>%</span></div>
          <div style={{ width: 120, height: afterH, background: `linear-gradient(180deg,${GOLD},#b88a4f)`, margin: "0 auto", borderRadius: 4, boxShadow: "0 6px 24px rgba(0,0,0,0.7)" }} />
          <div style={{ color: CREAM, fontSize: 20, marginTop: 14, letterSpacing: "0.1em", textShadow: TS }}>DRIED BEAN</div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── 10 · PARTICLE CIRCLES — proportional circles, number below each ──
// Japanese 347.86 · Europeans 351.78 · American 354.66
const ParticleCircles: React.FC<{ s: number }> = ({ s }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const op = win(t, 349.946, 361.98);
  const items = [
    { label: "JAPAN", um: "10 μm", d: 54, atS: 350.354 },
    { label: "EUROPE", um: "12–25 μm", d: 97, atS: 354.335 },
    { label: "USA", um: "30 μm", d: 162, atS: 357.198 },
  ];
  const titleOp = appear(t, 350.354, 0.2);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ position: "absolute", top: 86, color: GOLD, fontSize: 27, fontWeight: 700, letterSpacing: "0.18em", textShadow: TS, opacity: titleOp }}>PARTICLE SIZE BY MARKET</div>
      <div style={{ display: "flex", gap: 96, alignItems: "flex-end", marginTop: 50 }}>
        {items.map((it) => {
          const o = appear(t, it.atS, 0.16);
          const sc = spring({ frame: Math.max(0, frame - sToF(it.atS - s)), fps, config: { damping: 11, stiffness: 110 } });
          return (
            <div key={it.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: o }}>
              <div style={{ color: CREAM, fontSize: 22, fontWeight: 700, letterSpacing: "0.16em", marginBottom: 16, textShadow: TS }}>{it.label}</div>
              <div style={{
                width: it.d, height: it.d, borderRadius: "50%",
                background: "radial-gradient(circle at 38% 32%, #e8c9a4, #8b6647)",
                border: `3px solid ${CREAM}`, boxShadow: "0 6px 26px rgba(0,0,0,0.7)",
                transform: `scale(${sc})`,
              }} />
              <div style={{ color: "#fff", fontSize: 30, fontWeight: 800, marginTop: 16, textShadow: TS }}>{it.um}</div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ─── 11 · TEMP CURVE — draws in step with words 45/27/31 ──────────────
const TempCurve: React.FC<{ s: number }> = ({ s }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, 497.44, 509.89);
  const cW = 940, cH = 420, pad = 76, minY = 20, maxY = 50;
  const data = [
    { tWhen: 497.967, y: 45, label: "" },
    { tWhen: 498.279, y: 45, label: "45°" },
    { tWhen: 499.912, y: 38, label: "" },
    { tWhen: 501.604, y: 27, label: "27°" },
    { tWhen: 503.852, y: 28, label: "" },
    { tWhen: 506.032, y: 31, label: "31°" },
    { tWhen: 507.677, y: 31, label: "HOLD" },
  ];
  const xs = (i: number) => (i / (data.length - 1)) * (cW - pad * 2) + pad;
  const ys = (y: number) => cH - pad - ((y - minY) / (maxY - minY)) * (cH - pad * 2);
  const lastFull = Math.max(0, data.filter((d) => t >= d.tWhen).length - 1);
  const pts: string[] = [];
  for (let i = 0; i <= lastFull; i++) pts.push(`${xs(i)},${ys(data[i].y)}`);
  if (lastFull < data.length - 1) {
    const a = data[lastFull], b = data[lastFull + 1];
    const c = Math.max(0, Math.min(1, (t - a.tWhen) / (b.tWhen - a.tWhen)));
    pts.push(`${xs(lastFull) + (xs(lastFull + 1) - xs(lastFull)) * c},${ys(a.y) + (ys(b.y) - ys(a.y)) * c}`);
  }
  const titleOp = appear(t, 497.471, 0.2);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ position: "absolute", top: 80, color: CREAM, fontSize: 25, fontWeight: 700, letterSpacing: "0.18em", textShadow: TS, opacity: titleOp }}>TEMPERING · FORM V CRYSTAL</div>
      <svg width={cW} height={cH} style={{ marginTop: 46, overflow: "visible" }}>
        {[20, 30, 40, 50].map((v) => (
          <g key={v}>
            <line x1={pad} y1={ys(v)} x2={cW - pad} y2={ys(v)} stroke="rgba(255,255,255,0.18)" strokeWidth={1} />
            <text x={pad - 12} y={ys(v) + 6} textAnchor="end" fill={DIM} fontSize="15" fontFamily={FONT} style={{ filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.9))" }}>{v}°</text>
          </g>
        ))}
        {pts.length > 1 && <polyline points={pts.join(" ")} fill="none" stroke={GOLD} strokeWidth={4} strokeLinejoin="round" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 9px rgba(212,165,116,0.7))" }} />}
        {data.map((d, i) => d.label && (
          <g key={i} opacity={appear(t, d.tWhen, 0.18)}>
            <circle cx={xs(i)} cy={ys(d.y)} r={7} fill="#fff" stroke={GOLD} strokeWidth={3} />
            <text x={xs(i)} y={ys(d.y) - 18} textAnchor="middle" fill={CREAM} fontSize="20" fontWeight="800" letterSpacing="0.06em" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.95))" }}>{d.label}</text>
          </g>
        ))}
      </svg>
    </AbsoluteFill>
  );
};

// ─── 12 · DONUT — Dark draws on "Dark", Milk draws on "Milk" ───────────
// Dark 554.63 · solids 558.54 · Milk 561.53
const DonutDarkMilk: React.FC<{ s: number }> = ({ s }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, 558.561, 570.247);
  const r = 120, sw = 26;
  const circ = 2 * Math.PI * r;
  const darkDraw = interpolate(t, [558.561, 562.525], [0, 0.70 * circ], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const milkDraw = interpolate(t, [565.536, 567.375], [0, 0.25 * circ], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const darkOp = appear(t, 558.561, 0.16);
  const milkOp = appear(t, 565.536, 0.16);
  const titleOp = appear(t, 558.561, 0.2);
  const ring = (cx: number, draw: number, color: string, pct: string, o: number) => (
    <g opacity={o}>
      <circle cx={cx} cy={300} r={r} fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth={sw + 6} />
      <circle cx={cx} cy={300} r={r} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth={sw} />
      <circle cx={cx} cy={300} r={r} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeDasharray={`${draw} ${circ}`} transform={`rotate(-90 ${cx} 300)`} style={{ filter: "drop-shadow(0 0 6px rgba(0,0,0,0.6))" }} />
      <text x={cx} y={300} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="68" fontWeight="800" style={{ filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.95))" }}>{pct}</text>
    </g>
  );
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ position: "absolute", top: 78, color: CREAM, fontSize: 27, fontWeight: 700, letterSpacing: "0.18em", textShadow: TS, opacity: titleOp }}>COCOA SOLIDS · TYPICAL</div>
      <svg width={1100} height={560} style={{ overflow: "visible", marginTop: 30 }}>
        {ring(360, darkDraw, "#4a2c1a", "70%", darkOp)}
        {ring(740, milkDraw, "#c69a73", "25%", milkOp)}
        <text x={360} y={470} textAnchor="middle" fill={CREAM} fontSize="27" fontWeight="700" letterSpacing="3" opacity={darkOp} style={{ filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.95))" }}>DARK</text>
        <text x={740} y={470} textAnchor="middle" fill={CREAM} fontSize="27" fontWeight="700" letterSpacing="3" opacity={milkOp} style={{ filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.95))" }}>MILK</text>
      </svg>
    </AbsoluteFill>
  );
};

// ─── 13 · CRYSTAL FORMS — 6 forms appear on "Form I..VI", V locks on cue
// Form I-VI 461.32 · unstable 463.50 · Form VI 472.16 · Form V wins 475.65
const CrystalFormsGrid: React.FC<{ s: number }> = ({ s }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, 464.383, 482.977);
  const forms = ["I", "II", "III", "IV", "V", "VI"];
  const titleOp = appear(t, 464.383, 0.2);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: GOLD, fontSize: 25, letterSpacing: "0.18em", marginBottom: 32, textShadow: TS, fontWeight: 700, opacity: titleOp }}>COCOA BUTTER · 6 CRYSTAL FORMS</div>
      <div style={{ display: "flex", gap: 22 }}>
        {forms.map((f, i) => {
          const cellS = 464.789 + i * 0.28;        // each form appears as "I..VI" is read
          const o = appear(t, cellS, 0.16);
          const isV = f === "V";
          // I-IV flagged unstable at 463.5; VI flagged at 472.16; V wins at 475.65
          const unstable = i <= 3 && t >= 467.012;
          const vWin = isV && t >= 479.159;
          const viFlag = f === "VI" && t >= 475.568;
          const border = vWin ? GOLD : unstable ? "rgba(150,90,60,0.45)" : "rgba(160,130,93,0.4)";
          const glow = vWin ? "0 0 26px rgba(212,165,116,0.6)" : "none";
          const dim = (unstable || viFlag) && !vWin ? 0.5 : 1;
          return (
            <div key={f} style={{ textAlign: "center", opacity: o, padding: 22, border: `2px solid ${border}`, borderRadius: 8, background: vWin ? "rgba(212,165,116,0.12)" : "rgba(0,0,0,0.35)", boxShadow: glow, minWidth: 116 }}>
              <div style={{ color: vWin ? "#fff" : DIM, fontSize: 18, letterSpacing: "0.2em", marginBottom: 8, textShadow: TS, opacity: dim }}>FORM</div>
              <div style={{ color: vWin ? "#fff" : CREAM, fontSize: 56, fontWeight: 800, lineHeight: 1, textShadow: TS, opacity: dim }}>{f}</div>
              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textShadow: TS, height: 14,
                color: vWin ? GOLD : "#b35c3f",
                opacity: vWin ? 1 : unstable || viFlag ? 0.85 : 0 }}>
                {vWin ? "STABLE" : viFlag ? "TOO HARD" : "UNSTABLE"}
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// ─── 14 · END CARD ────────────────────────────────────────────────────
const EndCard: React.FC<{ s: number; atS: number; outS: number }> = ({ s, atS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const subOp = appear(t, atS + 0.6, 0.3);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: GOLD, fontSize: 26, letterSpacing: "0.3em", marginBottom: 22, textShadow: TS }}>FACTORY FILES</div>
      <div style={{ color: "#fff", fontSize: 60, fontWeight: 800, letterSpacing: "-1px", textShadow: TS, textAlign: "center", lineHeight: 1.12 }}>WE GO INSIDE<br />THE FACTORY</div>
      <div style={{ color: CREAM, fontSize: 22, letterSpacing: "0.15em", marginTop: 26, textShadow: TS, opacity: subOp }}>SUBSCRIBE · NEW VIDEO EVERY WEEK</div>
    </AbsoluteFill>
  );
};

// ════════════════════════════════════════════════════════════════════
// MASTER TIMELINE — every window tied to chocolate word_timestamps
// ════════════════════════════════════════════════════════════════════
type Item = { from: number; to: number; node: (s: number) => React.ReactNode };

const ITEMS: Item[] = [
  // ── PART 1 · HOOK (denser opening) ──
  { from: 2.425, to: 7.424, node: (s) => <BrandChips s={s} /> },
  { from: 8.102, to: 11.297, node: (s) => <HookText s={s} text="2 THINGS THEY DON'T TELL YOU" atS={8.365} outS={11.297} /> },
  { from: 11.297, to: 14.223, node: (s) => <Stamp s={s} title="U.S. LAW" sub="A GOVERNMENT ALLOWANCE" atS={11.724} outS={14.223} /> },
  { from: 17.641, to: 23.195, node: (s) => <EquationStack s={s} l1="INSECT FRAGMENTS" op="+" l2="RODENT HAIR" l1S={17.82} opS={19.043} l2S={19.222} outS={23.195} /> },

  // ── PART 2 · HEAVY METALS ──
  { from: 24.641, to: 30.729, node: (s) => <Stamp s={s} title="CONSUMER REPORTS" sub="2022 · 28 DARK BRANDS TESTED" atS={25.397} outS={30.729} /> },
  { from: 31.749, to: 37.196, node: (s) => <BigStat s={s} label="EVERY DARK BAR TESTED" value={100} valueS={32.203} suffix="%" sub="contained lead + cadmium" outS={37.196} /> },

  // ── PART 3 · WIEZE ──
  { from: 50.786, to: 58.513, node: (s) => <LowerThird s={s} name="WIEZE, BELGIUM" sub="BARRY CALLEBAUT PLANT" italic="largest chocolate factory on Earth" nameS={51.435} subS={52.398} outS={58.513} /> },

  // ── PART 4 · MESOAMERICA ──
  { from: 76.268, to: 83.222, node: (s) => <LowerThird s={s} name="MESOAMERICA" sub="THEOBROMA CACAO" italic="a bitter, spiced beverage" nameS={76.453} subS={77.734} outS={83.222} /> },
  { from: 86.544, to: 92.538, node: (s) => <EquationStack s={s} l1="1 TURKEY" op="=" l2="100 CACAO BEANS" l1S={87.201} opS={87.641} l2S={88.101} outS={92.538} /> },

  // ── PART 5 · VAN HOUTEN ──
  { from: 98.621, to: 107.109, node: (s) => <LowerThird s={s} name="COENRAAD VAN HOUTEN" sub="NETHERLANDS · 1828" italic="patented the hydraulic cocoa press" nameS={99.285} subS={100.305} outS={107.109} /> },

  // ── PART 6 · LINDT ──
  { from: 132.522, to: 140.163, node: (s) => <LowerThird s={s} name="RODOLPHE LINDT" sub="SWITZERLAND · 1879" italic="invented conching by accident" nameS={133.212} subS={133.641} outS={140.163} /> },
  { from: 144.902, to: 150.874, node: (s) => <BigStat s={s} label="ONE FORGOTTEN WEEKEND" value={72} valueS={145.072} suffix="hrs" sub="of continuous shearing" outS={150.874} /> },

  // ── PART 7 · STEP 1 + STEP 2 ──
  { from: 161.374, to: 166.131, node: (s) => <Chapter s={s} num="01" title="HARVESTING" sub="HAND-CUT WITH MACHETES" numS={161.755} titleS={162.406} outS={166.131} /> },
  { from: 182.408, to: 187.826, node: (s) => <TextHighlight s={s} pre="this step" hi="CANNOT BE AUTOMATED" post="" hiS={182.906} hiColor={GOLD} outS={187.826} /> },
  { from: 193.89, to: 199.097, node: (s) => <Chapter s={s} num="02" title="FERMENTATION" sub="5–7 DAYS UNDER BANANA LEAVES" numS={194.312} titleS={195.034} outS={199.097} /> },
  { from: 210.935, to: 216.112, node: (s) => <BigStat s={s} label="FERMENTATION PEAK" value={50} valueS={211.215} suffix="°C" sub="kills the bean's embryo" outS={216.112} /> },

  // ── PART 8 · STEP 3 + MOISTURE ──
  { from: 225.701, to: 230.549, node: (s) => <Chapter s={s} num="03" title="DRYING" sub="OPEN-AIR · 7 DAYS IN SUN" numS={226.746} titleS={227.083} outS={230.549} /> },
  { from: 232.621, to: 237.721, node: (s) => <MoistureCompare s={s} /> },
  { from: 251.085, to: 256.529, node: (s) => <TextHighlight s={s} pre="this is how" hi="LEAD" post="enters the chain" hiS={251.522} hiColor={CREAM} outS={256.529} /> },

  // ── PART 9 · STEP 4 ──
  { from: 261.291, to: 266.546, node: (s) => <Chapter s={s} num="04" title="ROASTING" sub="ROASTING + WINNOWING" numS={261.851} titleS={262.375} outS={266.546} /> },
  { from: 268.794, to: 274.18, node: (s) => <BigStat s={s} label="ROAST TEMPERATURE" value={140} valueS={269.224} suffix="°C" sub="20 – 40 minutes" outS={274.18} /> },
  { from: 292.303, to: 298.275, node: (s) => <BigStat s={s} label="FDA SHELL LIMIT" value={1.75} valueS={292.636} decimals={2} suffix="%" sub="maximum by weight" outS={298.275} /> },

  // ── PART 10 · STEP 5 ──
  { from: 310.537, to: 313.593, node: (s) => <Stamp s={s} title="SUBSCRIBE" sub="FOR THE REAL STORY" atS={311.01} outS={313.593} /> },
  { from: 313.593, to: 318.37, node: (s) => <Chapter s={s} num="05" title="GRINDING" sub="DOWN TO 10–30 MICRONS" numS={313.735} titleS={314.418} outS={318.37} /> },
  { from: 349.894, to: 362.084, node: (s) => <ParticleCircles s={s} /> },

  // ── PART 11 · STEP 6 ──
  { from: 369.137, to: 374.489, node: (s) => <Chapter s={s} num="06" title="CONCHING" sub="4 – 24 HOURS OF SHEARING" numS={369.666} titleS={370.462} outS={374.489} /> },
  { from: 411.273, to: 419.78, node: (s) => <LowerThird s={s} name="HERSHEY'S" sub="THE LIPOLYSIS PROCESS" italic="butyric acid — used since WWII" nameS={411.76} subS={412.766} outS={419.78} /> },
  { from: 426.011, to: 434.971, node: (s) => <EquationStack s={s} l1="BUTYRIC ACID" op="=" l2="THE SMELL OF VOMIT" l1S={426.291} opS={431.967} l2S={432.622} outS={434.971} /> },

  // ── PART 12 · STEP 7 ──
  { from: 447.353, to: 452.347, node: (s) => <Chapter s={s} num="07" title="TEMPERING" sub="FORCE THE FORM V CRYSTAL" numS={447.646} titleS={448.305} outS={452.347} /> },
  { from: 464.189, to: 483.076, node: (s) => <CrystalFormsGrid s={s} /> },
  { from: 497.255, to: 510.074, node: (s) => <TempCurve s={s} /> },

  // ── PART 13 · STEP 8 ──
  { from: 522.506, to: 527.259, node: (s) => <Chapter s={s} num="08" title="MOULDING" sub="MOULDING + PACKAGING" numS={523.147} titleS={523.53} outS={527.259} /> },

  // ── PART 14 · THE LEAD QUESTION ──
  { from: 557.877, to: 570.423, node: (s) => <DonutDarkMilk s={s} /> },
  { from: 575.81, to: 581.581, node: (s) => <Stamp s={s} title="PROP 65" sub="CALIFORNIA WARNING LABEL" atS={576.121} outS={581.581} color="#e6c167" /> },
  { from: 597.526, to: 606.787, node: (s) => <EquationStack s={s} l1="60 INSECT FRAGMENTS" op="+" l2="1 RODENT HAIR" l1S={597.75} opS={603.484} l2S={604.918} outS={606.787} /> },

  // ── PART 15 · WHITE CHOCOLATE ──
  { from: 637.708, to: 643.614, node: (s) => <TextHighlight s={s} pre="white chocolate =" hi="ZERO COCOA SOLIDS" post="" hiS={637.987} hiColor={CREAM} outS={643.614} /> },

  // ── PART 16 · OUTRO ──
  { from: 665.284, to: 670.579, node: (s) => <BigStat s={s} label="GLOBAL CHOCOLATE INDUSTRY" value={31.87} valueS={665.486} decimals={2} prefix="$" suffix="B" sub="annual · 2024" outS={670.579} /> },
  { from: 673.306, to: 679.206, node: (s) => <BigStat s={s} label="WORLD'S COCOA SUPPLY" value={70} valueS={673.559} suffix="%" sub="Ghana + Côte d'Ivoire" outS={679.206} /> },
  { from: 709.501, to: 717.306, node: (s) => <EndCard s={s} atS={710.067} outS={716.806} /> },
];

export const ChocolateFullPreview: React.FC = () => {
  return (
    <AbsoluteFill>
      {ITEMS.map((it, i) => (
        <Sequence key={i} from={sToF(it.from)} durationInFrames={Math.max(1, sToF(it.to) - sToF(it.from))} layout="none">
          {it.node(it.from)}
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
